import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { useTranslation } from "react-i18next"
import { useId } from "react"

import { CardContent } from "@/components/ui/card"
import { PageBody } from "@/components/ui/layout"
import type {
  NewSpecialityFormValues,
  NewSpecialityParams,
} from "@/types/educators/speciality"
import { SaveButton } from "@/components/shared/action-buttons"

import Form, { DetailsSection } from "./form"
import { ErrorHint } from "@/components/ui/errorhint"
import { FormGroup } from "@/components/ui/form-field"
import { ErrorMessage } from "@hookform/error-message"
import { Label } from "@radix-ui/react-label"
import { Controller, useFormContext, SubmitHandler } from "react-hook-form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useNewProps } from "./page-props"
import { SelectInput } from "@/components/ui/select"
import { useNavigate } from "@/lib/page-rendering"

export default function New() {
  const navigate = useNavigate()
  const id = useId()
  const { personal_listing_profile_id } = useNewProps()

  const onSubmit: SubmitHandler<NewSpecialityFormValues> = (data) => {
    const { speciality, speciality_scope, team_profile_id } = data
    const listing_profile_id =
      speciality_scope === "personal"
        ? personal_listing_profile_id
        : team_profile_id!
    navigate({
      href: ".",
      method: "post",
      data: {
        listing_profile_id,
        speciality,
      } satisfies NewSpecialityParams,
    })
  }

  return (
    <>
      <EducatorPageChrome action={<SaveButton form={id} />} />
      <PageBody>
        <CardContent>
          <Form onSubmit={onSubmit} id={id}>
            <SpecialityScope />
          </Form>
        </CardContent>
      </PageBody>
    </>
  )
}

function SpecialityScope() {
  const { control, formState, watch, setValue } =
    useFormContext<NewSpecialityFormValues>()
  const { t } = useTranslation(["specialities", "common"])

  const { team_profile_options } = useNewProps()

  const specialityScope = watch("speciality_scope")
  specialityScope || setValue("speciality_scope", "personal")

  return (
    <DetailsSection>
      <div className="space-y-2">
        <Label id="speciality-scope-label" htmlFor="speciality-scope-input">
          {t("specialities:new.speciality_scope.label")}
        </Label>
        <Controller
          control={control}
          name="speciality_scope"
          rules={{ required: t("common:errors.required") }}
          render={({ field: { onChange } }) => (
            <RadioGroup
              required
              aria-labelledby="speciality-scope-label"
              id="speciality-scope-input"
              value={specialityScope ?? ""}
              onValueChange={(value) => onChange(value)}
              className="items-top flex w-full flex-col space-y-2 space-x-2"
            >
              <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
                <div className="flex flex-row items-center">
                  <RadioGroupItem
                    value="personal"
                    id="personal"
                    className="peer"
                    aria-labelledby="personal-label"
                    aria-describedby="personal-description"
                  />
                  <Label
                    htmlFor="personal"
                    className="flex items-center gap-2 rounded-md px-4 py-2"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium" id="personal-label">
                        {t("specialities:new.speciality_scope.personal_label")}
                      </span>
                      <span className="text-xs" id="personal-description">
                        {t(
                          "specialities:new.speciality_scope.personal_description",
                        )}
                      </span>
                    </div>
                  </Label>
                </div>
              </div>

              <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
                <div className="flex flex-row items-center">
                  <RadioGroupItem
                    value="team"
                    id="team"
                    className="peer"
                    aria-labelledby="team-label"
                    aria-describedby="team-description"
                  />
                  <Label
                    htmlFor="team"
                    className="flex items-center gap-2 rounded-md px-4 py-2"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium" id="team-label">
                        {t("specialities:new.speciality_scope.team_label")}
                      </span>
                      <span className="text-xs" id="team-description">
                        {t(
                          "specialities:new.speciality_scope.team_description",
                        )}
                      </span>
                    </div>
                  </Label>
                </div>
                <FormGroup
                  htmlFor="team-select"
                  label={t("specialities:new.team_select.label")}
                >
                  <Controller
                    name="team_profile_id"
                    control={control}
                    render={({ field }) => (
                      <SelectInput
                        className="w-64"
                        id="team-select"
                        value={field.value}
                        onChange={field.onChange}
                        options={team_profile_options}
                        placeholder={t(
                          "specialities:new.team_select.placeholder",
                        )}
                      />
                    )}
                  />
                  <ErrorMessage
                    name="team_profile_id"
                    errors={formState.errors}
                    render={ErrorHint}
                  />
                </FormGroup>
              </div>
            </RadioGroup>
          )}
        />
        <ErrorMessage
          name="speciality_scope"
          errors={formState.errors}
          render={ErrorHint}
        />
      </div>
    </DetailsSection>
  )
}
